import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Table,
  Spin,
  Alert,
  Statistic,
  Tag,
  DatePicker,
  Tabs,
  Select,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FileTextOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  DollarOutlined,
  CarOutlined,
  ShoppingOutlined,
  AuditOutlined,
  BarChartOutlined,
  ToolOutlined,
  ExceptionOutlined,
  BankOutlined,
  InboxOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  getUnifiedDashboardActionSummary,
  type UnifiedDashboardActionSummary,
} from '@/services/unifiedDashboard.service';
import { getUnifiedTasks, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import { UnifiedTasksExecutionBoard } from '@/components/unified/UnifiedTasksExecutionBoard';
import { UnifiedTaskSavedViewsBar } from '@/components/unified/UnifiedTaskSavedViewsBar';
import { applySavedViewFilterRecord } from '@/utils/unifiedTaskSavedViewApply';
import type { CustomerOutstandingDetailRow, SupplierOutstandingDetailRow } from '@/services/financeDashboard.service';
import type {
  PendingApprovalPaymentVoucherRow,
  PendingApprovalGrnRow as FinPendingApprovalGrnRow,
  GrnNotInvoicedRow as FinGrnNotInvoicedRow,
} from '@/services/financeDashboard.service';
import {
  emptySupplyChainDashboardSummary,
  type PendingApprovalPRRow,
  type PendingApprovalBidAnalysisRow,
  type PendingApprovalPORow,
  type PendingApprovalGrnRow as ScmPendingApprovalGrnRow,
  type PendingApprovalReturnRow,
  type OverdueOrDueSoonPORow,
  type PRPendingSourcingRow,
  type GrnNotInvoicedRow as ScmGrnNotInvoicedRow,
} from '@/services/supplyChainDashboard.service';
import { emptyFinanceDashboardSummary } from '@/services/financeDashboard.service';
import type {
  PendingReviewSalesOrderRow,
  OrderInProgressRow,
  DeliveryNotInvoicedRow,
  SalesOrderPendingWorkOrderRow,
} from '@/services/salesDashboard.service';
import { emptySalesDashboardSummary } from '@/services/salesDashboard.service';

const { Title, Text, Paragraph } = Typography;

/** Query keys owned by the execution task board on this page (bookmarkable filters). */
const UNIFIED_TASK_URL_KEYS = [
  'module',
  'status',
  'priority',
  'task_type',
  'sdoc',
  'origin',
  'tq',
  'me',
  'unassigned',
  'team',
  'overdue',
  'due_today',
  'nodue',
  'open',
  'sla_risk',
  'blocked',
  'ready',
  'slasort',
] as const;

function stableSearchParamsString(sp: URLSearchParams): string {
  return [...sp.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

function parseUnifiedTaskUrl(sp: URLSearchParams): {
  module?: string;
  status?: string;
  priority?: string;
  taskType?: string;
  sdoc?: string;
  origin: 'all' | 'system' | 'manual';
  tq: string;
  assignedToMe: boolean;
  unassignedOnly: boolean;
  teamOthersOnly: boolean;
  overdueOnly: boolean;
  dueTodayOnly: boolean;
  noDueDateOnly: boolean;
  openTasksOnly: boolean;
  blockedOnly: boolean;
  readyToStart: boolean;
  slaRiskFilter: 'all' | 'safe' | 'warning' | 'breach' | 'critical' | 'none';
  sortBySla: boolean;
} {
  const o = sp.get('origin');
  const origin: 'all' | 'system' | 'manual' = o === 'manual' || o === 'system' ? o : 'all';
  const me = sp.get('me') === '1';
  const un = sp.get('unassigned') === '1';
  const team = sp.get('team') === '1';
  const sr = sp.get('sla_risk');
  const slaRiskFilter: 'all' | 'safe' | 'warning' | 'breach' | 'critical' | 'none' =
    sr === 'safe' || sr === 'warning' || sr === 'breach' || sr === 'critical' || sr === 'none' ? sr : 'all';
  return {
    module: sp.get('module') || undefined,
    status: sp.get('status') || undefined,
    priority: sp.get('priority') || undefined,
    taskType: sp.get('task_type') || undefined,
    sdoc: sp.get('sdoc') || undefined,
    origin,
    tq: sp.get('tq') || '',
    assignedToMe: me && !un && !team,
    unassignedOnly: un && !me && !team,
    teamOthersOnly: team && !me && !un,
    overdueOnly: sp.get('overdue') === '1',
    dueTodayOnly: sp.get('due_today') === '1',
    noDueDateOnly: sp.get('nodue') === '1',
    openTasksOnly: sp.get('open') === '1',
    blockedOnly: sp.get('blocked') === '1',
    readyToStart: sp.get('ready') === '1',
    slaRiskFilter,
    sortBySla: sp.get('slasort') === '1',
  };
}

const emptyUnified: UnifiedDashboardActionSummary = {
  finance: emptyFinanceDashboardSummary(),
  supply_chain: emptySupplyChainDashboardSummary(),
  sales: emptySalesDashboardSummary(),
};

export const UnifiedEnterpriseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatCurrency } = useCurrency();
  const [initialTaskUrl] = useState(() => parseUnifiedTaskUrl(new URLSearchParams(window.location.search)));
  const tu = initialTaskUrl;

  const [summary, setSummary] = useState<UnifiedDashboardActionSummary | null>(null);
  const [tasks, setTasks] = useState<UnifiedTaskRow[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOnDate, setAsOnDate] = useState<string | undefined>(undefined);
  const [taskModuleFilter, setTaskModuleFilter] = useState<string | undefined>(() => tu.module);
  const [assignedToMe, setAssignedToMe] = useState(() => tu.assignedToMe);
  const [overdueOnly, setOverdueOnly] = useState(() => tu.overdueOnly);
  const [dueTodayOnly, setDueTodayOnly] = useState(() => tu.dueTodayOnly);
  const [noDueDateOnly, setNoDueDateOnly] = useState(() => tu.noDueDateOnly);
  const [unassignedOnly, setUnassignedOnly] = useState(() => tu.unassignedOnly);
  const [teamOthersOnly, setTeamOthersOnly] = useState(() => tu.teamOthersOnly);
  const [taskSearchQ, setTaskSearchQ] = useState(() => tu.tq);
  const [taskSearchDebounced, setTaskSearchDebounced] = useState(() => tu.tq);
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>(() => tu.priority);
  const [taskTypeFilter, setTaskTypeFilter] = useState<string | undefined>(() => tu.taskType);
  const [sourceDocTypeFilter, setSourceDocTypeFilter] = useState<string | undefined>(() => tu.sdoc);
  const [taskOriginFilter, setTaskOriginFilter] = useState<'all' | 'system' | 'manual'>(() => tu.origin);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(() => tu.status);
  const [openTasksOnly, setOpenTasksOnly] = useState(() => tu.openTasksOnly);
  const [blockedOnly, setBlockedOnly] = useState(() => tu.blockedOnly);
  const [readyToStart, setReadyToStart] = useState(() => tu.readyToStart);
  const [slaRiskFilter, setSlaRiskFilter] = useState(() => tu.slaRiskFilter);
  const [sortBySla, setSortBySla] = useState(() => tu.sortBySla);

  const buildExecutionFiltersSnapshot = useCallback((): Record<string, unknown> => {
    const filters: Record<string, unknown> = {};
    if (taskModuleFilter) filters.module = taskModuleFilter;
    if (statusFilter) filters.status = statusFilter;
    if (priorityFilter) filters.priority = priorityFilter;
    if (taskTypeFilter) filters.task_type = taskTypeFilter;
    if (sourceDocTypeFilter) filters.sdoc = sourceDocTypeFilter;
    if (taskOriginFilter !== 'all') filters.origin = taskOriginFilter;
    const tq = taskSearchDebounced.trim();
    if (tq) filters.tq = tq.slice(0, 200);
    if (assignedToMe && !teamOthersOnly && !unassignedOnly) filters.me = true;
    if (unassignedOnly && !assignedToMe && !teamOthersOnly) filters.unassigned = true;
    if (teamOthersOnly && !assignedToMe && !unassignedOnly) filters.team = true;
    if (overdueOnly) filters.overdue = true;
    if (dueTodayOnly) filters.due_today = true;
    if (noDueDateOnly) filters.nodue = true;
    if (openTasksOnly) filters.open = true;
    if (blockedOnly) filters.blocked = true;
    if (readyToStart) filters.ready = true;
    if (slaRiskFilter !== 'all') filters.sla = slaRiskFilter;
    if (sortBySla) filters.sla_sort = true;
    return filters;
  }, [
    taskModuleFilter,
    statusFilter,
    priorityFilter,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    taskSearchDebounced,
    assignedToMe,
    teamOthersOnly,
    unassignedOnly,
    overdueOnly,
    dueTodayOnly,
    noDueDateOnly,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const applyExecutionFilters = useCallback((f: Record<string, unknown>) => {
    applySavedViewFilterRecord(f, {
      setTaskModuleFilter: setTaskModuleFilter,
      setStatusFilter: setStatusFilter,
      setPriorityFilter: setPriorityFilter,
      setTaskTypeFilter: setTaskTypeFilter,
      setSourceDocTypeFilter: setSourceDocTypeFilter,
      setTaskOriginFilter: setTaskOriginFilter,
      setTaskSearchQ: setTaskSearchQ,
      setTaskSearchDebounced: setTaskSearchDebounced,
      setOverdueOnly: setOverdueOnly,
      setDueTodayOnly: setDueTodayOnly,
      setNoDueDateOnly: setNoDueDateOnly,
      setOpenTasksOnly: setOpenTasksOnly,
      assignment: {
        setAssignedToMe: setAssignedToMe,
        setUnassignedOnly: setUnassignedOnly,
        setTeamOthersOnly: setTeamOthersOnly,
      },
      setBlockedOnly: setBlockedOnly,
      setReadyToStart: setReadyToStart,
      setSlaRiskFilter: setSlaRiskFilter,
      setSortBySla: setSortBySla,
    });
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setTaskSearchDebounced(taskSearchQ), 400);
    return () => clearTimeout(id);
  }, [taskSearchQ]);

  const fetchSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    getUnifiedDashboardActionSummary(asOnDate ? { as_on_date: asOnDate } : undefined)
      .then(setSummary)
      .catch((e: any) => setError(e?.message ?? 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [asOnDate]);

  const fetchTasks = useCallback((opts?: { sync?: boolean }) => {
    setTasksLoading(true);
    getUnifiedTasks({
      sync: opts?.sync === true,
      as_on_date: asOnDate,
      module: taskModuleFilter,
      assigned_to: assignedToMe && !teamOthersOnly && !unassignedOnly ? 'me' : undefined,
      overdue_only: overdueOnly || undefined,
      due_today_only: dueTodayOnly || undefined,
      no_due_date_only: noDueDateOnly || undefined,
      unassigned_only: unassignedOnly || undefined,
      team_others_only: teamOthersOnly || undefined,
      q: taskSearchDebounced.trim() || undefined,
      priority: priorityFilter,
      task_type: taskTypeFilter,
      source_document_type: sourceDocTypeFilter,
      origin: taskOriginFilter === 'all' ? undefined : taskOriginFilter,
      status: statusFilter,
      open_only: openTasksOnly && !statusFilter ? true : undefined,
      sla_risk: slaRiskFilter !== 'all' ? slaRiskFilter : undefined,
      blocked_only: blockedOnly || undefined,
      ready_to_start: readyToStart || undefined,
      sort: sortBySla ? 'sla' : undefined,
      limit: 500,
      offset: 0,
    })
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false));
  }, [
    asOnDate,
    taskModuleFilter,
    assignedToMe,
    overdueOnly,
    dueTodayOnly,
    noDueDateOnly,
    unassignedOnly,
    teamOthersOnly,
    taskSearchDebounced,
    priorityFilter,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    statusFilter,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const setModuleFilter = useCallback((m: string | undefined) => {
    setTaskModuleFilter(m);
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    UNIFIED_TASK_URL_KEYS.forEach((k) => next.delete(k));
    if (taskModuleFilter) next.set('module', taskModuleFilter);
    if (statusFilter) next.set('status', statusFilter);
    if (priorityFilter) next.set('priority', priorityFilter);
    if (taskTypeFilter) next.set('task_type', taskTypeFilter);
    if (sourceDocTypeFilter) next.set('sdoc', sourceDocTypeFilter);
    if (taskOriginFilter !== 'all') next.set('origin', taskOriginFilter);
    const tq = taskSearchDebounced.trim();
    if (tq) next.set('tq', tq.slice(0, 200));
    if (assignedToMe && !teamOthersOnly && !unassignedOnly) next.set('me', '1');
    if (unassignedOnly && !assignedToMe && !teamOthersOnly) next.set('unassigned', '1');
    if (teamOthersOnly && !assignedToMe && !unassignedOnly) next.set('team', '1');
    if (overdueOnly) next.set('overdue', '1');
    if (dueTodayOnly) next.set('due_today', '1');
    if (noDueDateOnly) next.set('nodue', '1');
    if (openTasksOnly) next.set('open', '1');
    if (slaRiskFilter !== 'all') next.set('sla_risk', slaRiskFilter);
    if (blockedOnly) next.set('blocked', '1');
    if (readyToStart) next.set('ready', '1');
    if (sortBySla) next.set('slasort', '1');
    if (stableSearchParamsString(next) === stableSearchParamsString(searchParams)) return;
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    taskModuleFilter,
    statusFilter,
    priorityFilter,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    taskSearchDebounced,
    assignedToMe,
    unassignedOnly,
    teamOthersOnly,
    overdueOnly,
    dueTodayOnly,
    noDueDateOnly,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (summary) fetchTasks();
  }, [summary, fetchTasks, taskModuleFilter]);

  if (loading && !summary) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const u = summary ?? emptyUnified;
  const fin = u.finance;
  const scm = u.supply_chain;
  const sales = u.sales;

  const overdueArCount = fin.overdue_customer_invoice_count ?? fin.overdue_customer_invoices.length;
  const overdueApCount = fin.overdue_supplier_invoice_count ?? fin.overdue_supplier_invoices.length;
  const priorityCount =
    overdueArCount +
    overdueApCount +
    (scm.overdue_po_count ?? scm.overdue_pos.length) +
    fin.pending_approval_payment_vouchers.length +
    fin.pending_approval_journal_vouchers.length +
    fin.pending_approval_grns.length +
    scm.pending_approval_prs.length +
    scm.pending_approval_bid_analyses.length +
    scm.pending_approval_pos.length +
    scm.pending_approval_grns.length +
    scm.pending_approval_returns.length;

  const overdueArColumns: ColumnsType<CustomerOutstandingDetailRow> = [
    { title: 'Invoice', dataIndex: 'invoice_number', key: 'invoice_number', width: 110 },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
    { title: 'Due', dataIndex: 'due_date', key: 'due_date', width: 95 },
    { title: 'Days', dataIndex: 'overdue_days', key: 'overdue_days', width: 60, render: (d: number) => <Tag color="red">{d}</Tag> },
    { title: 'Balance', dataIndex: 'balance_due', key: 'balance_due', align: 'right', width: 95, render: (v: number) => formatCurrency(v ?? 0) },
    { title: '', key: 'action', width: 70, render: (_: unknown, r: CustomerOutstandingDetailRow) => <Button type="link" size="small" onClick={() => navigate(`/growth/invoices/${r.invoice_id}`)}>View</Button> },
  ];
  const overdueApColumns: ColumnsType<SupplierOutstandingDetailRow> = [
    { title: 'Bill', dataIndex: 'invoice_number', key: 'invoice_number', width: 110 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
    { title: 'Due', dataIndex: 'due_date', key: 'due_date', width: 95 },
    { title: 'Days', dataIndex: 'overdue_days', key: 'overdue_days', width: 60, render: (d: number) => <Tag color="red">{d}</Tag> },
    { title: 'Balance', dataIndex: 'balance_due', key: 'balance_due', align: 'right', width: 95, render: (v: number) => formatCurrency(v ?? 0) },
    { title: '', key: 'action', width: 70, render: (_: unknown, r: SupplierOutstandingDetailRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-invoices/${r.invoice_id}`)}>View</Button> },
  ];
  const overduePoColumns: ColumnsType<OverdueOrDueSoonPORow> = [
    { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 110 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
    { title: 'Delivery', dataIndex: 'delivery_date', key: 'delivery_date', width: 95 },
    { title: 'Days', dataIndex: 'overdue_days', key: 'overdue_days', width: 60, render: (d: number) => <Tag color="red">{d ?? '-'}</Tag> },
    { title: 'Amount', dataIndex: 'grand_total', key: 'grand_total', align: 'right', width: 95, render: (v: number) => formatCurrency(v ?? 0) },
    { title: '', key: 'action', width: 70, render: (_: unknown, r: OverdueOrDueSoonPORow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-orders/${r.id}`)}>View</Button> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <Title level={3}>Unified Enterprise Execution Dashboard</Title>
            <Paragraph type="secondary">
              Central control tower: Finance, Supply Chain, and Sales actions in one place. What needs to be done now, what is overdue, and what requires approval.
            </Paragraph>
          </div>
          <Space wrap>
            <Text type="secondary">As on date:</Text>
            <DatePicker
              value={asOnDate ? dayjs(asOnDate) : dayjs()}
              onChange={(d) => setAsOnDate(d ? d.format('YYYY-MM-DD') : undefined)}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}
        <Alert
          type="info"
          showIcon
          message="Cross-module workboard"
          description="One place for Finance, Supply Chain, and Sales execution. Start with Priority queue and Exceptions; sync tasks to refresh your worklist."
        />

        {/* Exceptions & Risks */}
        {summary && tasks.filter((t) => t.priority === 'Critical' || t.status === 'Escalated').length > 0 && (
          <Card
            size="small"
            title={<Space><WarningOutlined style={{ color: '#cf1322' }} /><span>Exceptions &amp; risks</span><Tag color="red">{tasks.filter((t) => t.priority === 'Critical' || t.status === 'Escalated').length}</Tag></Space>}
          >
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={tasks.filter((t) => t.priority === 'Critical' || t.status === 'Escalated')}
              columns={[
                { title: 'Type', dataIndex: 'task_type', key: 'task_type', width: 100 },
                { title: 'Module', dataIndex: 'module', key: 'module', width: 90 },
                { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 80, render: (p: string) => <Tag color="red">{p}</Tag> },
                { title: 'Status', dataIndex: 'status', key: 'status', width: 90 },
                { title: 'Overdue (days)', dataIndex: 'overdue_days', key: 'overdue_days', width: 95, render: (d: number) => d != null ? <Tag color="red">{d}</Tag> : null },
                { title: 'Action', dataIndex: 'recommended_action', key: 'recommended_action', ellipsis: true },
                {
                  title: '',
                  key: 'action',
                  width: 70,
                  render: (_: unknown, r: UnifiedTaskRow) => (
                    <Button type="link" size="small" onClick={() => r.action_link && navigate(r.action_link)}>View</Button>
                  ),
                },
              ]}
            />
          </Card>
        )}

        {/* Execution tasks — Kanban / table (unified_tasks API) */}
        {summary && (
          <UnifiedTasksExecutionBoard
            tasks={tasks}
            loading={tasksLoading}
            onRefresh={() => fetchTasks({ sync: true })}
            moduleFilter={taskModuleFilter}
            onModuleFilterChange={setModuleFilter}
            assignedToMe={assignedToMe}
            onAssignedToMeChange={(v) => {
              setAssignedToMe(v);
              if (v) {
                setTeamOthersOnly(false);
                setUnassignedOnly(false);
              }
            }}
            overdueOnly={overdueOnly}
            onOverdueOnlyChange={setOverdueOnly}
            dueTodayOnly={dueTodayOnly}
            onDueTodayOnlyChange={setDueTodayOnly}
            noDueDateOnly={noDueDateOnly}
            onNoDueDateOnlyChange={setNoDueDateOnly}
            priorityFilter={priorityFilter}
            onPriorityFilterChange={setPriorityFilter}
            taskTypeFilter={taskTypeFilter}
            onTaskTypeFilterChange={setTaskTypeFilter}
            sourceDocumentTypeFilter={sourceDocTypeFilter}
            onSourceDocumentTypeFilterChange={setSourceDocTypeFilter}
            taskOriginFilter={taskOriginFilter}
            onTaskOriginFilterChange={setTaskOriginFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            openTasksOnly={openTasksOnly}
            onOpenTasksOnlyChange={setOpenTasksOnly}
            searchQuery={taskSearchQ}
            onSearchQueryChange={setTaskSearchQ}
            unassignedOnly={unassignedOnly}
            onUnassignedOnlyChange={(v) => {
              setUnassignedOnly(v);
              if (v) {
                setAssignedToMe(false);
                setTeamOthersOnly(false);
              }
            }}
            teamOthersOnly={teamOthersOnly}
            onTeamOthersOnlyChange={(v) => {
              setTeamOthersOnly(v);
              if (v) {
                setAssignedToMe(false);
                setUnassignedOnly(false);
              }
            }}
            blockedOnly={blockedOnly}
            onBlockedOnlyChange={setBlockedOnly}
            readyToStart={readyToStart}
            onReadyToStartChange={setReadyToStart}
            slaRiskFilter={slaRiskFilter}
            onSlaRiskFilterChange={setSlaRiskFilter}
            sortBySla={sortBySla}
            onSortBySlaChange={setSortBySla}
            allowManualCreate
            titleExtra={
              <UnifiedTaskSavedViewsBar
                buildFiltersSnapshot={buildExecutionFiltersSnapshot}
                applyFiltersSnapshot={applyExecutionFilters}
              />
            }
          />
        )}

        {/* Quick Actions */}
        <Card size="small" title={<Space><ThunderboltOutlined /><span>Quick Actions</span></Space>}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/orders/new')}>Create order</Button>
            <Button icon={<CarOutlined />} onClick={() => navigate('/growth/delivery-notes')}>Delivery notes</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/invoices')}>Create invoice</Button>
            <Button icon={<DollarOutlined />} onClick={() => navigate('/fin/receipt-vouchers/new')}>Record receipt</Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/fin/reports/customer-statement')}>Customer statement</Button>
            <Button icon={<BarChartOutlined />} onClick={() => navigate('/fin/reports/customer-outstanding')}>Outstanding report</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-requests/new')}>Create PR</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-orders/new')}>Create PO</Button>
            <Button icon={<InboxOutlined />} onClick={() => navigate('/scm/procurement/grn/new')}>Create GRN</Button>
            <Button icon={<BankOutlined />} onClick={() => navigate('/fin/receipt-vouchers/new')}>New Receipt</Button>
            <Button icon={<BankOutlined />} onClick={() => navigate('/fin/payment-vouchers/new')}>New Payment</Button>
          </Space>
        </Card>

        {/* Priority Action Queue */}
        <Card
          size="small"
          title={<Space><WarningOutlined style={{ color: '#cf1322' }} /><span>Priority Action Queue</span><Tag color="red">{priorityCount} items</Tag></Space>}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Statistic title="Overdue AR" value={overdueArCount} valueStyle={{ color: overdueArCount > 0 ? '#cf1322' : undefined }} />
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/fin/dashboard')}>Follow up</Button>
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Overdue AP" value={overdueApCount} valueStyle={{ color: overdueApCount > 0 ? '#cf1322' : undefined }} />
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/fin/dashboard')}>Follow up</Button>
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Overdue POs" value={(scm.overdue_po_count ?? scm.overdue_pos.length)} valueStyle={{ color: (scm.overdue_po_count ?? scm.overdue_pos.length) > 0 ? '#cf1322' : undefined }} />
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/scm/dashboard')}>Follow up</Button>
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Pending approvals" value={fin.pending_approval_payment_vouchers.length + fin.pending_approval_journal_vouchers.length + fin.pending_approval_grns.length + scm.pending_approval_prs.length + scm.pending_approval_bid_analyses.length + scm.pending_approval_pos.length + scm.pending_approval_grns.length + scm.pending_approval_returns.length} prefix={<ExceptionOutlined />} />
            </Col>
          </Row>
        </Card>

        {/* Overdue — Tabs */}
        <Card size="small" title={<Space><WarningOutlined /><span>Overdue</span></Space>}>
          <Tabs
            items={[
              {
                key: 'ar',
                label: `AR (${overdueArCount})`,
                children: (
                  <Table
                    rowKey="invoice_id"
                    size="small"
                    pagination={false}
                    dataSource={fin.overdue_customer_invoices}
                    columns={overdueArColumns}
                  />
                ),
              },
              {
                key: 'ap',
                label: `AP (${overdueApCount})`,
                children: (
                  <Table rowKey="invoice_id" size="small" pagination={false} dataSource={fin.overdue_supplier_invoices} columns={overdueApColumns} />
                ),
              },
              {
                key: 'po',
                label: `POs (${(scm.overdue_po_count ?? scm.overdue_pos.length)})`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {(scm.overdue_po_count ?? 0) > scm.overdue_pos.length ? (
                      <Text type="secondary">
                        Showing {scm.overdue_pos.length} of {scm.overdue_po_count}. Open Supply Chain Control Tower for the full follow-up queue.
                      </Text>
                    ) : null}
                    <Table rowKey="id" size="small" pagination={false} dataSource={scm.overdue_pos} columns={overduePoColumns} />
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/scm/dashboard')}>
                      Open delayed PO follow-up
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        {/* Approvals Pending */}
        {(fin.pending_approval_payment_vouchers.length > 0 || fin.pending_approval_journal_vouchers.length > 0 || fin.pending_approval_grns.length > 0 || scm.pending_approval_prs.length > 0 || scm.pending_approval_bid_analyses.length > 0 || scm.pending_approval_pos.length > 0 || scm.pending_approval_grns.length > 0 || scm.pending_approval_returns.length > 0) && (
          <Card size="small" title={<Space><ExceptionOutlined /><span>Approvals Pending</span></Space>}>
            <Row gutter={[16, 16]}>
              {fin.pending_approval_payment_vouchers.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Payment vouchers</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={fin.pending_approval_payment_vouchers}
                    columns={[
                      { title: 'Voucher', dataIndex: 'voucher_number', key: 'voucher_number', width: 120 },
                      { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (v: number) => formatCurrency(v ?? 0) },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingApprovalPaymentVoucherRow) => <Button type="link" size="small" onClick={() => navigate(`/fin/payment-vouchers/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {fin.pending_approval_journal_vouchers.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Journal vouchers</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={fin.pending_approval_journal_vouchers}
                    columns={[
                      { title: 'Voucher', dataIndex: 'voucher_number', key: 'voucher_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: () => <Button type="link" size="small" onClick={() => navigate('/fin/gl/journals')}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {fin.pending_approval_grns.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>GRNs (Finance)</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={fin.pending_approval_grns}
                    columns={[
                      { title: 'GRN', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: FinPendingApprovalGrnRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/grn/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {scm.pending_approval_prs.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Purchase requests</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={scm.pending_approval_prs}
                    columns={[
                      { title: 'PR #', dataIndex: 'pr_number', key: 'pr_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingApprovalPRRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-requests/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {scm.pending_approval_bid_analyses.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Bid analyses</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={scm.pending_approval_bid_analyses}
                    columns={[
                      { title: 'Analysis #', dataIndex: 'analysis_number', key: 'analysis_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingApprovalBidAnalysisRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/bid-analyses/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {scm.pending_approval_pos.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Purchase orders</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={scm.pending_approval_pos}
                    columns={[
                      { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 120 },
                      { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingApprovalPORow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-orders/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {scm.pending_approval_grns.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>GRNs (SCM)</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={scm.pending_approval_grns}
                    columns={[
                      { title: 'GRN', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: ScmPendingApprovalGrnRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/grn/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
              {scm.pending_approval_returns.length > 0 && (
                <Col xs={24} md={8}>
                  <Text strong>Purchase returns</Text>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={scm.pending_approval_returns}
                    columns={[
                      { title: 'Return #', dataIndex: 'return_number', key: 'return_number', width: 120 },
                      { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingApprovalReturnRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-returns/${r.id}`)}>View</Button> },
                    ]}
                  />
                </Col>
              )}
            </Row>
          </Card>
        )}

        {/* Finance Actions */}
        <Card size="small" title={<Space><DollarOutlined /><span>Finance Actions</span></Space>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Overdue AR" value={overdueArCount} prefix={<WarningOutlined />} valueStyle={{ color: overdueArCount > 0 ? '#cf1322' : undefined }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Overdue AP" value={overdueApCount} prefix={<WarningOutlined />} valueStyle={{ color: overdueApCount > 0 ? '#cf1322' : undefined }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Draft receipts" value={fin.draft_receipt_vouchers_count} />
              {fin.draft_receipt_vouchers_count > 0 && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/fin/receipt-vouchers')}>Open</Button>}
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Draft payments" value={fin.draft_payment_vouchers_count} />
              {fin.draft_payment_vouchers_count > 0 && <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/fin/payment-vouchers')}>Open</Button>}
            </Col>
          </Row>
          {fin.grn_not_invoiced.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginTop: 12 }}>GRN not invoiced</Text>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={fin.grn_not_invoiced}
                columns={[
                  { title: 'GRN', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                  { title: 'Date', dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                  { title: '', key: 'action', width: 70, render: (_: unknown, r: FinGrnNotInvoicedRow) => <Button type="link" size="small" onClick={() => navigate('/scm/procurement/grn/' + r.id)}>View</Button> },
                ]}
              />
            </>
          )}
        </Card>

        {/* Supply Chain Actions */}
        <Card size="small" title={<Space><InboxOutlined /><span>Supply Chain Actions</span></Space>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Overdue POs" value={(scm.overdue_po_count ?? scm.overdue_pos.length)} prefix={<WarningOutlined />} valueStyle={{ color: (scm.overdue_po_count ?? scm.overdue_pos.length) > 0 ? '#cf1322' : undefined }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Due soon POs" value={(scm.due_soon_po_count ?? scm.due_soon_pos.length)} prefix={<ClockCircleOutlined />} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="PRs pending sourcing" value={scm.prs_pending_sourcing.length} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Draft PR/PO/GRN" value={scm.draft_pr_count + scm.draft_po_count + scm.draft_grn_count} />
            </Col>
          </Row>
          {((scm.overdue_po_count ?? scm.overdue_pos.length) > 0 || (scm.due_soon_po_count ?? scm.due_soon_pos.length) > 0 || scm.prs_pending_sourcing.length > 0 || scm.grn_not_invoiced.length > 0) && (
            <Tabs
              style={{ marginTop: 12 }}
              items={[
                {
                  key: 'overdue-po',
                  label: 'Overdue POs',
                  children: (scm.overdue_po_count ?? scm.overdue_pos.length) === 0 ? <Text type="secondary">None</Text> : (
                    <Table rowKey="id" size="small" pagination={false} dataSource={scm.overdue_pos} columns={overduePoColumns} />
                  ),
                },
                {
                  key: 'due-soon-po',
                  label: 'Due soon POs',
                  children: (scm.due_soon_po_count ?? scm.due_soon_pos.length) === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={scm.due_soon_pos}
                      columns={[
                        { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 120 },
                        { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                        { title: 'Delivery', dataIndex: 'delivery_date', key: 'delivery_date', width: 100 },
                        { title: 'Amount', dataIndex: 'grand_total', key: 'grand_total', align: 'right' as const, render: (v: number) => formatCurrency(v ?? 0) },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: OverdueOrDueSoonPORow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-orders/${r.id}`)}>View</Button> },
                      ]}
                    />
                  ),
                },
                {
                  key: 'pr-sourcing',
                  label: 'PRs pending sourcing',
                  children: scm.prs_pending_sourcing.length === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={scm.prs_pending_sourcing}
                      columns={[
                        { title: 'PR #', dataIndex: 'pr_number', key: 'pr_number', width: 120 },
                        { title: 'Required', dataIndex: 'required_date', key: 'required_date', width: 100 },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: PRPendingSourcingRow) => <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-requests/${r.id}`)}>View</Button> },
                      ]}
                    />
                  ),
                },
                {
                  key: 'grn-ni',
                  label: 'GRN not invoiced',
                  children: scm.grn_not_invoiced.length === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={scm.grn_not_invoiced}
                      columns={[
                        { title: 'GRN', dataIndex: 'grn_number', key: 'grn_number', width: 120 },
                        { title: 'Date', dataIndex: 'grn_date', key: 'grn_date', width: 100 },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: ScmGrnNotInvoicedRow) => <Button type="link" size="small" onClick={() => navigate('/scm/procurement/grn/' + r.id)}>View</Button> },
                      ]}
                    />
                  ),
                },
              ]}
            />
          )}
        </Card>

        {/* Sales Actions */}
        <Card size="small" title={<Space><ShoppingOutlined /><span>Sales Actions</span></Space>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Pending review SO" value={sales.pending_review_sales_orders.length} prefix={<AuditOutlined />} valueStyle={{ color: sales.pending_review_sales_orders.length > 0 ? '#faad14' : undefined }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Orders in progress" value={sales.orders_in_progress.length} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="SOs pending WO"
                value={sales.sales_orders_pending_work_order.length}
                prefix={<ToolOutlined />}
                valueStyle={{
                  color: sales.sales_orders_pending_work_order.length > 0 ? '#faad14' : undefined,
                }}
              />
              {sales.sales_orders_pending_work_order.length > 0 && (
                <Space size="small" wrap>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => navigate('/growth/dashboard#pending-work-order')}
                  >
                    Control Tower
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() =>
                      navigate('/unified?task_type=Operational&sdoc=SalesOrder&open=1')
                    }
                  >
                    Tasks
                  </Button>
                </Space>
              )}
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Deliveries not invoiced" value={sales.deliveries_not_invoiced.length} prefix={<CarOutlined />} valueStyle={{ color: sales.deliveries_not_invoiced.length > 0 ? '#faad14' : undefined }} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Draft SO / Invoices" value={sales.draft_sales_order_count + sales.draft_sales_invoice_count} />
            </Col>
          </Row>
          {(sales.pending_review_sales_orders.length > 0 ||
            sales.orders_in_progress.length > 0 ||
            sales.sales_orders_pending_work_order.length > 0 ||
            sales.deliveries_not_invoiced.length > 0) && (
            <Tabs
              style={{ marginTop: 12 }}
              items={[
                {
                  key: 'pending-so',
                  label: 'Pending review (draft SO)',
                  children: sales.pending_review_sales_orders.length === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={sales.pending_review_sales_orders}
                      columns={[
                        { title: 'SO #', dataIndex: 'sales_order_number', key: 'sales_order_number', width: 120 },
                        { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
                        { title: 'Total', dataIndex: 'grand_total', key: 'grand_total', align: 'right' as const, width: 100, render: (v: number) => formatCurrency(v ?? 0) },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: PendingReviewSalesOrderRow) => <Button type="link" size="small" onClick={() => navigate(`/growth/orders/${r.id}`)}>View</Button> },
                      ]}
                    />
                  ),
                },
                {
                  key: 'orders-progress',
                  label: 'Orders in progress',
                  children: sales.orders_in_progress.length === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={sales.orders_in_progress}
                      columns={[
                        { title: 'SO #', dataIndex: 'sales_order_number', key: 'sales_order_number', width: 120 },
                        { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
                        { title: 'Status', dataIndex: 'order_status', key: 'order_status', width: 100 },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: OrderInProgressRow) => <Button type="link" size="small" onClick={() => navigate(`/growth/orders/${r.id}`)}>View</Button> },
                      ]}
                    />
                  ),
                },
                {
                  key: 'pending-wo',
                  label: 'Pending work order',
                  children:
                    sales.sales_orders_pending_work_order.length === 0 ? (
                      <Text type="secondary">None</Text>
                    ) : (
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={sales.sales_orders_pending_work_order}
                        columns={[
                          {
                            title: 'SO #',
                            dataIndex: 'sales_order_number',
                            key: 'sales_order_number',
                            width: 120,
                          },
                          {
                            title: 'Customer',
                            dataIndex: 'customer_name',
                            key: 'customer_name',
                            ellipsis: true,
                          },
                          {
                            title: 'Days waiting',
                            dataIndex: 'days_since_approved',
                            key: 'days_since_approved',
                            width: 100,
                            render: (d: number) => (d > 0 ? <Tag color="orange">{d}d</Tag> : '—'),
                          },
                          {
                            title: '',
                            key: 'action',
                            width: 120,
                            render: (_: unknown, r: SalesOrderPendingWorkOrderRow) => (
                              <Button
                                type="link"
                                size="small"
                                onClick={() => navigate(`/growth/orders/${r.id}/create-work-order`)}
                              >
                                Create WO
                              </Button>
                            ),
                          },
                        ]}
                      />
                    ),
                },
                {
                  key: 'dn-not-inv',
                  label: 'Deliveries not invoiced',
                  children: sales.deliveries_not_invoiced.length === 0 ? <Text type="secondary">None</Text> : (
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={false}
                      dataSource={sales.deliveries_not_invoiced}
                      columns={[
                        { title: 'DN #', dataIndex: 'delivery_note_number', key: 'delivery_note_number', width: 120 },
                        { title: 'Delivery date', dataIndex: 'delivery_date', key: 'delivery_date', width: 110 },
                        { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true },
                        { title: '', key: 'action', width: 70, render: (_: unknown, r: DeliveryNotInvoicedRow) => <Button type="link" size="small" onClick={() => navigate(`/growth/delivery-notes/${r.id}`)}>View</Button> },
                      ]}
                    />
                  ),
                },
              ]}
            />
          )}
        </Card>
      </Space>
    </div>
  );
};
